import type { ReadonlyURLSearchParams } from 'next/navigation'

import type {
  StoreFrontDto,
  StoreFrontOptionsDefaultViewEnum,
} from '@b-stock/listing-api-client'

import { getSingleParam } from '@/buyer-portal/helpers/demandParam'

import { isView } from './typeInspectors'
import {
  type ListingsSort,
  type ListingSearchState,
  type QueryParams,
  LISTINGS_SORT_END_TIME,
  LISTINGS_SORT_RELEVANCE,
  LISTINGS_SORT_RECOMMENDED,
} from './types'

export const LISTING_SEARCH_DEFAULT_PAGE_SIZE: Record<
  StoreFrontOptionsDefaultViewEnum,
  number
> = {
  GRID: 20,
  LIST: 80,
}

export const getDefaultSort = ({
  hasQuery = false,
  view = 'GRID',
}: {
  hasQuery?: boolean
  view?: StoreFrontOptionsDefaultViewEnum
}): ListingsSort => {
  if (view === 'LIST') return LISTINGS_SORT_END_TIME
  if (hasQuery) return LISTINGS_SORT_RELEVANCE
  return LISTINGS_SORT_RECOMMENDED
}

export const getView = ({
  pageDefaultView,
  searchParams,
  storefront,
}: {
  pageDefaultView?: StoreFrontOptionsDefaultViewEnum
  searchParams?: QueryParams | ReadonlyURLSearchParams | null
  storefront?: StoreFrontDto | null
}): StoreFrontOptionsDefaultViewEnum => {
  const viewParam = (getSingleParam(searchParams, 'view') ?? '').toUpperCase()
  const view = isView(viewParam)
    ? viewParam
    : (storefront?.options?.defaultView ?? pageDefaultView ?? 'GRID')
  return view
}

export const getDefaultState = ({
  view = 'GRID',
}: {
  view?: StoreFrontOptionsDefaultViewEnum
} = {}): ListingSearchState => {
  const [sortBy, sortOrder] = getDefaultSort({ view })

  return {
    page: 1,
    sortBy,
    sortOrder,
    appliedFromSavedSearch: false,
    applyingSearchQuery: false,
    id: null,
    isSortFrozen: false,
    pageSize: LISTING_SEARCH_DEFAULT_PAGE_SIZE[view],
    view,
    filters: {
      category: [],
      categories: [],
      storefront: [],
      storeFrontId: [],
      condition: [],
      inventoryType: [],
      productType: [],
      manufacturer: [],
      model: [],
      capacity: [],
      memory: [],
      carrier: [],
      sellerGrade: [],
      deals: [],
      starterLots: false,
      isCreditCardEnabled: false,
      eligibleOnly: false,
      myBids: false,
      bidStatus: undefined,
      closesIn: undefined,
      listingType: [],
      buyingFormat: [],
      currentBidRanges: [null, null],
      msrp: [null, null],
      msrpPercentage: null,
      landedCostPercentage: null,
      units: [null, null],
      problemFreeOrderRate: null,
      categoryThreshold: null,
      transportMode: [],
      allowedShipmentTypes: [],
      region: [],
      warehouseLocation: [],
      distanceRange: {
        distance: null,
        coordinates: null,
        zipCode: null,
      },
      endTimeRange: [null, null],
      q: null,
    },
  }
}

// Static default for non-dynamic use cases
export const defaultState = getDefaultState()
